import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { Close, Inventory } from '@mui/icons-material';
import HelmSpinner from './HelmSpinner';
import { API_URL, fetchWithCredentials } from '../config';

const SKUModal = ({ sku, open, onClose }) => {
  const [skuDetails, setSkuDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && sku) {
      fetchSkuDetails();
    }
  }, [open, sku]);

  const fetchSkuDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch from product_info dataset
      const response = await fetchWithCredentials(`${API_URL}/api/dataset/product_info`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch product data: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Find the SKU in the data
      const product = data.data?.find(p => p.sku_id === sku);
      
      if (product) {
        setSkuDetails(product);
      } else {
        setError('SKU not found in catalog');
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderDetailRow = (label, value, isChip = false) => {
    if (!value) return null;
    
    return (
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={4}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {label}:
          </Typography>
        </Grid>
        <Grid item xs={8}>
          {isChip ? (
            <Chip label={value} size="small" variant="outlined" />
          ) : (
            <Typography variant="body2">
              {value}
            </Typography>
          )}
        </Grid>
      </Grid>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory color="primary" />
          <Typography variant="h6">SKU Details</Typography>
          <Chip label={sku} variant="outlined" sx={{ fontFamily: 'monospace' }} />
        </Box>
        <Button onClick={onClose} size="small">
          <Close />
        </Button>
      </DialogTitle>
      
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <HelmSpinner size={48} showText={true} />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {skuDetails && (
          <Box>
            {/* Product Name */}
            {skuDetails.item_description && (
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {skuDetails.item_description}
                </Typography>
              </Box>
            )}
            
            <Divider sx={{ mb: 3 }} />
            
            {/* Basic Info */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Product Information
            </Typography>
            
            {renderDetailRow('Brand', skuDetails.brand, true)}
            {renderDetailRow('Category', skuDetails.mh2_category, true)}
            {renderDetailRow('Sub Category', skuDetails.mh1_sub_category)}
            {renderDetailRow('Department', skuDetails.department_name)}
            {renderDetailRow('Gender', skuDetails.gender, true)}
            
            <Divider sx={{ my: 2 }} />
            
            {/* Product Attributes */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Product Attributes
            </Typography>
            
            {renderDetailRow('Material', skuDetails.mat_content)}
            {renderDetailRow('Color', skuDetails.color_code_name_short, true)}
            {renderDetailRow('Color (Full)', skuDetails.color_code_name_long)}
            {renderDetailRow('Style Code', skuDetails.style_code, true)}
            {renderDetailRow('Sizes', skuDetails.sizes_concatenated)}
            
            <Divider sx={{ my: 2 }} />
            
            {/* Technical Specs */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Technical Specifications
            </Typography>
            
            {renderDetailRow('Technology', skuDetails.technologies_desc)}
            {renderDetailRow('Water Resistance', skuDetails.water_resistance_level, true)}
            {renderDetailRow('Warmth Rating', skuDetails.warmth_rating, true)}
            
            {!skuDetails.item_description && !skuDetails.brand && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Limited product information available for this SKU.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SKUModal;
